/**
 * Legal / advertising compliance helpers (mercado hispanohablante).
 *
 * - datos del prestador (entidad + identificador fiscal) — desde ENV
 * - patrones de copy prohibido (promesas tipo «garantizado» que infringen la
 *   normativa de publicidad engañosa en ES/UE y LatAm)
 *
 * ⚠️ TODO legal: rellenar la entidad real cuando se constituya (p. ej. US LLC).
 * Por defecto los identificadores fiscales van VACÍOS (ENV) — así las páginas
 * no muestran números inventados hasta que existan los reales.
 */

import { CONTACTS } from "@/lib/contacts";

export const ADVERTISER_INFO = {
  // TODO legal: sustituir por la razón social real de la entidad (US LLC / etc.).
  legalName: process.env.NEXT_PUBLIC_ADVERTISER_LEGAL_NAME ?? "Zaruvia LLC",
  // Identificador fiscal de la entidad (EIN / NIF / etc.) — vacío hasta constituir.
  inn: process.env.NEXT_PUBLIC_ADVERTISER_TAX_ID ?? "",
  // Número de registro mercantil de la entidad — vacío hasta constituir.
  ogrn: process.env.NEXT_PUBLIC_ADVERTISER_REG_NUMBER ?? "",
  ogrnLabel: "Reg.",
  // Fuente única de contactos — src/lib/contacts.ts (CONTACTS.general).
  email: CONTACTS.general,
  website: "zaruvia.com",
};

/**
 * Frases que pueden constituir publicidad engañosa (normativa ES/UE de consumo
 * y publicidad; criterios análogos en LatAm). Se escanea el copy en build-time.
 */
export const BANNED_COPY_PATTERNS: RegExp[] = [
  /garantiza(?:do|dos|mos|r|)\b/i,
  /100\s?%\s?(garant|exact|segur|éxito|acierto)/i,
  /predicci[oó]n(?:es)?\s+exact/i,
  /cambia(?:r[aá]?|)\s+tu\s+vida/i,
  /descubre\s+tu\s+futuro/i,
  /predi(?:go|ce|cen|remos|rá)\b/i,
  /destino\s+(?:exact|predeterminad|asegurad)/i,
  /resultado\s+asegurad/i,
];

export interface CopyIssue {
  phrase: string;
  pattern: string;
  suggestion: string;
}

/** Palabras de negación que convierten una promesa en un descargo legítimo. */
const NEGATION_BEFORE = /\b(no|nunca|jamás|ni|sin)\s+(?:se\s+|te\s+)?$/i;

export function auditCopy(text: string): CopyIssue[] {
  const issues: CopyIssue[] = [];
  for (const re of BANNED_COPY_PATTERNS) {
    const flags = re.flags.includes("g") ? re.flags : re.flags + "g";
    const gre = new RegExp(re.source, flags);
    let m: RegExpExecArray | null;
    while ((m = gre.exec(text)) !== null) {
      // Ignora negaciones: «no garantiza», «no predice el futuro» son descargos, no promesas.
      const before = text.slice(Math.max(0, m.index - 12), m.index);
      if (NEGATION_BEFORE.test(before)) continue;
      issues.push({ phrase: m[0], pattern: re.source, suggestion: suggestReplacement(m[0]) });
      break; // un hallazgo por patrón basta
    }
  }
  return issues;
}

/**
 * Sugerencia de reemplazo para la frase detectada.
 *
 * ⚠️ Corregido 2026-07: estas ramas comprobaban subcadenas RUSAS ("гаранти",
 * "предсказа"…) mientras `phrase` es siempre la coincidencia ESPAÑOLA de
 * BANNED_COPY_PATTERNS → nunca acertaban y todo caía al default genérico.
 * Al tocar los patrones de arriba, actualiza también estas ramas.
 */
function suggestReplacement(phrase: string): string {
  const lower = phrase.toLowerCase();
  // "100 %…" primero: la frase puede contener además "garant"/"exact".
  if (lower.includes("100")) return "alta precisión / resultado fiable";
  if (lower.includes("garantiz")) return "ayuda / brinda la oportunidad";
  if (lower.includes("predicci") || lower.includes("predi") || lower.includes("futuro"))
    return "recomendaciones personales / pronóstico basado en la fecha de nacimiento";
  if (lower.includes("cambia")) return "ayuda a comprenderte / herramienta de autoconocimiento";
  if (lower.includes("asegurad") || lower.includes("predeterminad") || lower.includes("destino"))
    return "orientación / lectura orientativa, sin promesas de resultado";
  return "una formulación más suave";
}

/**
 * Standard disclaimer text required on every product landing.
 * "Информационный характер" — key legal shield for numerology/astrology content in RU.
 */
export const STANDARD_DISCLAIMER =
  "Los materiales tienen carácter informativo y no sustituyen la consulta con profesionales. " +
  "La numerología y la astrología son herramientas de autoconocimiento, no medios de ayuda médica, " +
  "jurídica, financiera ni psicológica.";

/** Age-gate label for product pages that touch financial topics */
export const FINANCIAL_DISCLAIMER =
  "Las recomendaciones sobre dinero y carrera no constituyen asesoramiento financiero ni de inversión.";

/** Etiqueta de identificación de anuncio (para creatividades pagadas). */
export function buildAdLabel(ref: string): string {
  const parts = ["Publicidad"];
  if (ADVERTISER_INFO.legalName) parts.push(ADVERTISER_INFO.legalName);
  if (ADVERTISER_INFO.inn) parts.push(`NIF ${ADVERTISER_INFO.inn}`);
  if (ref) parts.push(`ref: ${ref}`);
  return parts.join(" · ");
}
/** @deprecated usa buildAdLabel — alias temporal. */
export const buildErIdLabel = buildAdLabel;
